import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Sun, Battery, Zap, TrendingDown, Home, Phone, ArrowRight, 
  CheckCircle, MapPin, Award, Shield, Users, Clock, HelpCircle,
  DollarSign, Leaf, BarChart3, ChevronDown
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { getCouncilBySlug } from '../data/councilData';
import { companyInfo } from '../data/mock';

const CouncilLandingPage = () => {
  const { councilSlug } = useParams();
  const council = getCouncilBySlug(councilSlug);

  if (!council) {
    return (
      <div className="min-h-screen">
        <Header />
        <main className="pt-32 pb-16 text-center">
          <h1 className="heading-1">Council not found</h1>
          <Link to="/" className="text-[#B5D43B] hover:underline mt-4 inline-block">
            Return to Home
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const faqs = [
    {
      question: `Is solar worth it in ${council.name}?`,
      answer: `Absolutely! With ${council.solarAdoption}% of homes already having solar and average savings of $${council.annualSavings}/year, ${council.name} residents are seeing excellent returns on their solar investment. The combination of good sun exposure and rising electricity costs makes solar a smart choice.`
    },
    {
      question: "How much can I save on electricity?",
      answer: `${council.name} households typically save up to ${council.billReduction}% on their electricity bills with solar. Adding a battery can increase savings further by using stored energy during peak evening hours when electricity is most expensive.`
    },
    {
      question: "Can I add a battery later?",
      answer: "Yes! Many homeowners start with solar panels and add a battery later. We design systems to be battery-ready, making future upgrades straightforward. Battery adoption in your area is growing at " + council.batteryGrowth + "% year-on-year."
    },
    {
      question: "Do I need council approval?",
      answer: `Most residential solar installations in ${council.name} don't require council approval as they're considered compliant development. However, heritage overlays or specific circumstances may require permits. We handle all approval requirements as part of our service.`
    },
    {
      question: "How long does installation take?",
      answer: "A typical residential solar installation takes just 1-2 days. Battery installations may add an additional day. From initial quote to switch-on, the entire process usually takes 2-4 weeks, depending on your distributor's connection timeframes."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={`Solar & Battery Installation in ${council.name}, VIC`}
        description={`Solar panels, battery storage and home electrification for ${council.name} (${council.region}) households. Save up to ${council.billReduction}% on power bills with Supa Energy Hub — accredited installers, rebate handling and the Supa Promise.`}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.question,
              acceptedAnswer: { '@type': 'Answer', text: f.answer },
            })),
          },
        ]}
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-28 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-[#B5D43B]" />
                <span className="text-sm font-semibold text-[#B5D43B]">{council.region}</span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1D1D1B] leading-tight mb-4">
                Solar & Battery Solutions in{' '}
                <span className="text-[#3D5A47]">{council.name}, VIC</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 mb-8">
                Join thousands of households in {council.name} already reducing their power bills 
                with solar and battery storage.
              </p>

              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                  <Sun className="w-8 h-8 text-[#B5D43B] mb-2" />
                  <p className="text-2xl md:text-3xl font-bold text-[#3D5A47]">{council.solarAdoption}%</p>
                  <p className="text-xs md:text-sm text-gray-500">Homes with solar</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                  <DollarSign className="w-8 h-8 text-[#B5D43B] mb-2" />
                  <p className="text-2xl md:text-3xl font-bold text-[#3D5A47]">${council.annualSavings}</p>
                  <p className="text-xs md:text-sm text-gray-500">Avg yearly savings</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                  <TrendingDown className="w-8 h-8 text-[#B5D43B] mb-2" />
                  <p className="text-2xl md:text-3xl font-bold text-[#3D5A47]">{council.billReduction}%</p>
                  <p className="text-xs md:text-sm text-gray-500">Bill reduction</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                  <Battery className="w-8 h-8 text-[#B5D43B] mb-2" />
                  <p className="text-2xl md:text-3xl font-bold text-[#3D5A47]">{council.batteryGrowth}%</p>
                  <p className="text-xs md:text-sm text-gray-500">Battery growth YoY</p>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary gap-2 text-base px-8 py-4 h-auto w-full sm:w-auto flex items-center">
                    <span className="flex flex-col items-start leading-tight">
                      <span>Get a Free Solar & Battery Estimate</span>
                      <span className="text-xs opacity-80 font-normal">Takes only 30 secs</span>
                    </span>
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button className="btn-secondary gap-2 text-base px-8 py-4 h-auto w-full sm:w-auto">
                    See Local Installations
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Why Solar Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#3D5A47] mb-4">
                Why Solar & Batteries Make Sense in {council.name}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {council.whySolar}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gray-50 rounded-2xl p-6">
                <Sun className="w-10 h-10 text-[#B5D43B] mb-4" />
                <h3 className="font-semibold text-[#3D5A47] mb-2">High Solar Uptake</h3>
                <p className="text-sm text-gray-600">
                  {council.solarAdoption}% of {council.name} homes already have solar, showing strong local confidence in the technology.
                </p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-6">
                <Home className="w-10 h-10 text-[#B5D43B] mb-4" />
                <h3 className="font-semibold text-[#3D5A47] mb-2">Ideal Housing</h3>
                <p className="text-sm text-gray-600">
                  {council.housingType} — perfect for maximising solar generation and savings.
                </p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-6">
                <TrendingDown className="w-10 h-10 text-[#B5D43B] mb-4" />
                <h3 className="font-semibold text-[#3D5A47] mb-2">Rising Costs</h3>
                <p className="text-sm text-gray-600">
                  Metro Victoria electricity prices continue to rise, making solar increasingly valuable each year.
                </p>
              </div>
              <div className="bg-gray-50 rounded-2xl p-6">
                <Battery className="w-10 h-10 text-[#B5D43B] mb-4" />
                <h3 className="font-semibold text-[#3D5A47] mb-2">Battery Boom</h3>
                <p className="text-sm text-gray-600">
                  Battery adoption growing {council.batteryGrowth}% year-on-year as residents seek evening energy independence.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Electrification Benefits */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#3D5A47] mb-4">
                Complete Home Electrification
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Solar is just the beginning. Combine panels with batteries and efficient electric appliances 
                for maximum savings and comfort.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Solar Panels */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="w-14 h-14 rounded-2xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                  <Sun className="w-7 h-7 text-[#3D5A47]" />
                </div>
                <h3 className="text-xl font-bold text-[#3D5A47] mb-3">Solar Panels</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Reduce daytime energy costs by generating your own power</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Typical system size in {council.name}: {council.avgSystemSize}</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Government rebates reduce upfront costs significantly</span>
                  </li>
                </ul>
              </div>

              {/* Battery Storage */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="w-14 h-14 rounded-2xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                  <Battery className="w-7 h-7 text-[#3D5A47]" />
                </div>
                <h3 className="text-xl font-bold text-[#3D5A47] mb-3">Battery Storage</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Use stored solar energy during expensive peak hours</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Blackout protection keeps essential appliances running</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Popular size in {council.name}: {council.avgBatterySize}</span>
                  </li>
                </ul>
              </div>

              {/* Full Electrification */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <div className="w-14 h-14 rounded-2xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                  <Zap className="w-7 h-7 text-[#3D5A47]" />
                </div>
                <h3 className="text-xl font-bold text-[#3D5A47] mb-3">Full Electrification</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Replace gas with efficient heat pumps and induction</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Add EV chargers powered by your solar</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span>Long-term cost stability with no gas bills</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Energy Flow Diagram */}
            <div className="mt-12 bg-white rounded-2xl p-6 md:p-8 border border-gray-100">
              <h3 className="text-lg font-semibold text-[#3D5A47] mb-6 text-center">How Energy Flows in Your Home</h3>
              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-2">
                    <Sun className="w-8 h-8 text-[#B5D43B]" />
                  </div>
                  <p className="text-sm font-medium text-[#3D5A47]">Solar Panels</p>
                  <p className="text-xs text-gray-500">Generate power</p>
                </div>
                <ArrowRight className="w-6 h-6 text-[#B5D43B] rotate-90 md:rotate-0" />
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-2">
                    <Home className="w-8 h-8 text-[#B5D43B]" />
                  </div>
                  <p className="text-sm font-medium text-[#3D5A47]">Your Home</p>
                  <p className="text-xs text-gray-500">Use instantly</p>
                </div>
                <ArrowRight className="w-6 h-6 text-[#B5D43B] rotate-90 md:rotate-0" />
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-[#B5D43B]/20 flex items-center justify-center mx-auto mb-2">
                    <Battery className="w-8 h-8 text-[#B5D43B]" />
                  </div>
                  <p className="text-sm font-medium text-[#3D5A47]">Battery</p>
                  <p className="text-xs text-gray-500">Store excess</p>
                </div>
                <ArrowRight className="w-6 h-6 text-[#B5D43B] rotate-90 md:rotate-0" />
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                    <Zap className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-[#3D5A47]">Grid</p>
                  <p className="text-xs text-gray-500">Export surplus</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Savings Snapshot */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#3D5A47] mb-4">
                Local Savings Snapshot
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                See what {council.name} residents are saving with solar and battery systems.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-[#3D5A47] to-[#2a3e31] rounded-2xl p-6 text-white text-center">
                <BarChart3 className="w-10 h-10 mx-auto mb-4 text-[#B5D43B]" />
                <p className="text-sm opacity-80 mb-2">Average Annual Savings</p>
                <p className="text-4xl font-bold mb-2">${council.annualSavings}</p>
                <p className="text-xs opacity-60">Based on {council.avgSystemSize} system</p>
              </div>
              
              <div className="bg-gradient-to-br from-[#B5D43B] to-[#9bc32f] rounded-2xl p-6 text-[#3D5A47] text-center">
                <TrendingDown className="w-10 h-10 mx-auto mb-4" />
                <p className="text-sm opacity-80 mb-2">Bill Reduction</p>
                <p className="text-4xl font-bold mb-2">{council.billReduction}%</p>
                <p className="text-xs opacity-70">Typical household</p>
              </div>
              
              <div className="bg-gradient-to-br from-[#3D5A47] to-[#2a3e31] rounded-2xl p-6 text-white text-center">
                <Clock className="w-10 h-10 mx-auto mb-4 text-[#B5D43B]" />
                <p className="text-sm opacity-80 mb-2">Payback Period</p>
                <p className="text-4xl font-bold mb-2">3-5</p>
                <p className="text-xs opacity-60">Years typical</p>
              </div>
            </div>

            <p className="text-center text-xs text-gray-500 mt-6 max-w-2xl mx-auto">
              ⚠️ Savings vary based on usage, system size, tariffs, and household behaviour. 
              Get a personalised estimate for accurate projections.
            </p>
          </div>
        </section>

        {/* Local Projects */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#3D5A47] mb-4">
                Recent Projects in {council.name}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                See real installations we've completed for {council.name} residents.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {council.projects.map((project, index) => (
                <div key={index} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="bg-gradient-to-r from-[#3D5A47] to-[#4a6b54] p-4">
                    <div className="flex items-center gap-2 text-white">
                      <MapPin className="w-4 h-4" />
                      <span className="font-medium">{project.suburb}, {council.name}</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 flex items-center gap-2">
                          <Sun className="w-4 h-4 text-[#B5D43B]" />
                          System Size
                        </span>
                        <span className="font-semibold text-[#3D5A47]">{project.systemSize}</span>
                      </div>
                      {project.batterySize && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-500 flex items-center gap-2">
                            <Battery className="w-4 h-4 text-[#B5D43B]" />
                            Battery
                          </span>
                          <span className="font-semibold text-[#3D5A47]">{project.batterySize}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500 flex items-center gap-2">
                          <Home className="w-4 h-4 text-[#B5D43B]" />
                          Home Type
                        </span>
                        <span className="font-semibold text-[#3D5A47]">{project.homeType}</span>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Est. Annual Savings</span>
                        <span className="text-lg font-bold text-[#B5D43B]">~${project.savings}/yr</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{project.brand}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/projects">
                <Button className="btn-secondary gap-2">
                  View All Our Projects
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#3D5A47] mb-4">
                Why Choose Supa Energy Hub
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Your trusted local partner for solar, batteries, and complete home electrification.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#3D5A47]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#3D5A47] mb-1">Local Victorian Expertise</h3>
                  <p className="text-sm text-gray-600">We know {council.name} and metro Melbourne inside out.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                  <Leaf className="w-6 h-6 text-[#3D5A47]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#3D5A47] mb-1">Clean Energy Specialists</h3>
                  <p className="text-sm text-gray-600">Solar, batteries, heat pumps, EV chargers — it's all we do.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-[#3D5A47]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#3D5A47] mb-1">End-to-End Service</h3>
                  <p className="text-sm text-gray-600">From design to install to ongoing support — one team.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-[#3D5A47]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#3D5A47] mb-1">Quality & Compliance</h3>
                  <p className="text-sm text-gray-600">CEC accredited, NETCC approved, fully licensed.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-[#3D5A47]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#3D5A47] mb-1">Premium Products</h3>
                  <p className="text-sm text-gray-600">Only tier-1 panels and batteries with solid warranties.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#3D5A47]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#3D5A47] mb-1">Ongoing Support</h3>
                  <p className="text-sm text-gray-600">We're here for the life of your system, not just the sale.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#3D5A47] mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600">
                Common questions from {council.name} residents about solar and batteries.
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <details key={index} className="group bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="font-medium text-[#3D5A47] pr-4">{faq.question}</span>
                    <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                  </summary>
                  <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to see what solar and battery could look like for your home in {council.name}?
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Join the {council.solarAdoption}% of {council.name} households already saving with solar. 
              Get your free, personalised estimate today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2 text-base px-8 py-4 h-auto">
                  <span className="flex flex-col items-start leading-tight">
                    <span>Get a Free Estimate</span>
                    <span className="text-xs opacity-80 font-normal">Takes only 30 secs</span>
                  </span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <a href={`tel:${companyInfo.phone}`}>
                <Button className="bg-white/10 hover:bg-white/20 text-white border-0 gap-2 text-base px-8 py-4 h-auto">
                  <Phone className="w-5 h-5" />
                  Speak to a Local Expert
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CouncilLandingPage;
